import type { ComponentPropsWithRef, PropsWithChildren } from "react";
import { css, styled } from "styled-components";

import { UIButton } from "common/components/UIButton";

const commonTagStyles = css`
  transition: background-color 0.3s ease;
  color: ${({ theme }) => theme.colors.chipText};
  background-color: ${({ theme }) => theme.colors.chipBackground};
  padding: 10px 20px;
  border-radius: 20px;
  margin: 10px 10px 0 0;
  font-weight: normal;
  border: 1px solid ${({ theme }) => theme.colors.chipBorder};
  display: inline-block;
  align-items: center;
  cursor: default;
  white-space: nowrap;
  text-overflow: ellipsis;
  /* we need to provide an explicit width, otherwise ellipsis wouldn't work */
  max-width: 250px;
  overflow: hidden;
  text-decoration: none;

  & > svg {
    margin-left: 0.5rem;
  }
`;

const StyledUIButton = styled(UIButton)<{ isActive: boolean }>`
  ${commonTagStyles}

  color: ${({ isActive, theme }) => (isActive ? theme.colors.chipActiveText : theme.colors.chipText)};
  background-color: ${({ isActive, theme }) =>
    isActive ? theme.colors.chipActiveBackground : theme.colors.chipBackground};
  cursor: pointer;

  &:focus-visible,
  &:hover {
    background-color: ${({ theme, isActive }) =>
      isActive ? theme.colors.chipActiveHoverBackground : theme.colors.chipHoverBackground};
  }
`;

const StyledTag = styled.div`
  ${commonTagStyles}
`;

type UITagButtonProps = PropsWithChildren & {
  className?: string;
  dataTestId?: string;
  isDisabled?: boolean;
  isActive?: boolean;
  title?: string;
  ariaDescription?: string;
  lang?: string;
  onClick?: () => void;
};

export const UITagButton = ({
  children,
  className,
  dataTestId,
  isDisabled = false,
  isActive = false,
  title,
  ariaDescription,
  lang,
  onClick,
}: UITagButtonProps) => (
  <StyledUIButton
    className={className}
    data-test-id={dataTestId}
    data-test-is-active={isActive}
    disabled={isDisabled}
    isActive={isActive}
    onClick={onClick}
    title={title}
    aria-description={ariaDescription}
    lang={lang}
  >
    {children}
  </StyledUIButton>
);

export const UITag = ({ children, ...rest }: ComponentPropsWithRef<"div">) => (
  <StyledTag {...rest}>{children}</StyledTag>
);
